import React, { useMemo } from 'react';
import { Col, Row, Text } from 'components';
import { FontAwesome5 } from '@expo/vector-icons';
import { StyleSheet } from 'react-native';
import { useDashboardStore } from 'store/Dashboard.Store';
import moment from 'moment';

const WARNING_COLOR = '#E94A36';

interface OldestOpenIssueData {
  orderNumber: string;
  customerName: string;
  timeSinceReport: string;
}

interface OldestOpenIssueCardProps {
  useMockData?: boolean;
}

const OldestOpenIssueCard: React.FC<OldestOpenIssueCardProps> = ({ useMockData = false }) => {
  const oldestOpenIssue = useDashboardStore((state) => state.oldestOpenIssue);

  // Mock data for testing
  const mockIssue: OldestOpenIssueData = {
    orderNumber: '#ORD-2828',
    customerName: 'ProClean Supplies',
    timeSinceReport: '3h 47m',
  };

  const issue = useMemo<OldestOpenIssueData | null>(() => {
    // Use mock data if enabled
    if (useMockData) {
      return mockIssue;
    }

    if (!oldestOpenIssue) {
      return null;
    }

    const { issue: issueData, order: orderData } = oldestOpenIssue;

    // Calculate time since report
    const createdAt = moment(issueData.createdAt);
    const now = moment();
    const duration = moment.duration(now.diff(createdAt));

    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();

    let timeSinceReport = '';
    if (hours > 0) {
      timeSinceReport = `${hours}h ${minutes}m`;
    } else {
      timeSinceReport = `${minutes}m`;
    }

    // Get order name/number
    const orderName = orderData.rawData?.name || `#${orderData.id}`;

    // Get customer/company name
    const customerName = orderData.rawData?.purchasingEntity?.company?.name
      || orderData.rawData?.purchasingEntity?.location?.name
      || orderData.rawData?.customer?.name
      || 'Unknown';

    return {
      orderNumber: orderName,
      customerName: customerName,
      timeSinceReport,
    };
  }, [oldestOpenIssue, useMockData]);

  if (!issue) {
    return (
      <Col style={styles.card} flex1>
        <Text fontSize={18} semiBold color="#4B5563" style={{ letterSpacing: 0.5 }} marginBottom={8}>
          OLDEST OPEN ISSUE
        </Text>
        <Text fontSize={14} color="#9CA3AF">No open issues</Text>
      </Col>
    );
  }

  return (
    <Col style={styles.card} flex1>
      <Text fontSize={18} semiBold color="#4B5563" style={{ letterSpacing: 0.5 }}>
        OLDEST OPEN ISSUE
      </Text>
      <Text fontSize={24} bold marginVertical={8} style={{ color: WARNING_COLOR }}>
        {issue.timeSinceReport}
      </Text>

      <Col style={styles.detailsBlock} marginBottom={8}>
        <Text fontSize={12} color="#6B7280" marginBottom={4}>
          ORDER {issue.orderNumber}
        </Text>
        <Text fontSize={16} bold color="#1F2937">
          {issue.customerName}
        </Text>
      </Col>
    </Col>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: 'white',
    padding: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 1,
  },
  detailsBlock: {
    backgroundColor: '#F9FAFB',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
});

export default OldestOpenIssueCard;
